// FEAT-6 — one entry point for running a coding question's code in the
// browser, whatever its language. The RECRUITER sets the language per
// question (`allowed_languages` on the frozen snapshot); nothing here lets a
// candidate choose.
//
//   python      → Pyodide on the main thread, exactly as before (pyodide.ts,
//                 untouched — it stays the Python twin of pyodide_grader.js)
//   javascript  → QuickJS-ng in a Web Worker   } engineWorker.ts + worker.ts,
//   sql         → sql.js (SQLite) in a Web Worker } sharing canon.js with the
//                                                  server graders
//   typescript  → FEAT-12: sucrase strips the types in the same worker, then
//                 the JavaScript path (types are never checked)
//   c, cpp, go, php, ruby, perl → FEAT-12: NOT here — they run on our code
//                 runner, through serverRun.ts; there is nothing to load in the tab.
import { getPyodide, runUserCodeAgainstTest as runPython, type RunResult } from "@/lib/pyodide";
import type { Language } from "./detect";
import { engineFor } from "./engineWorker";
import type { WorkerLanguage } from "./protocol";
import { isJsContract, RUN_LOCATION } from "./registry";

export type { RunResult };
export { detectFunctionName, languageOf, SUPPORTED_LANGUAGES } from "./detect";
export type { Language } from "./detect";
export {
  CONTRACT,
  EDITOR_MODE,
  isServerLanguage,
  languageEntry,
  languageLabel,
  REGISTRY,
  RUN_LOCATION,
  SANDBOX_NAME,
} from "./registry";

/** Load (or start loading) the runtime for `language`. Rejects if it cannot
 *  load; a later call retries rather than returning the cached failure.
 *  FEAT-12: a server language has no runtime in the tab — it resolves at once,
 *  so the "sandbox didn't load" state can never appear for one. */
export async function getRuntime(language: Language): Promise<void> {
  if (RUN_LOCATION[language] === "server") return;
  if (language === "python") {
    await getPyodide();
    return;
  }
  await engineFor(language as WorkerLanguage).warm();
}

export interface RunOptions {
  code: string;
  testInput: string;
  expectedOutput: string;
  /** Python / JavaScript / TypeScript: the function the test calls (`detectFunctionName`). */
  functionName?: string;
  /** SQL: the question's schema + seed rows, run before `code`. */
  setupCode?: string | null;
}

/** Run one test against the code in `language` and say whether it passed. */
export function runUserCodeAgainstTest(
  language: Language,
  options: RunOptions,
): Promise<RunResult> {
  const { code, testInput, expectedOutput, functionName = "solution", setupCode = null } = options;
  if (RUN_LOCATION[language] === "server") {
    // FEAT-12: every caller returns early to serverRun.ts for these.
    return Promise.reject(new Error(`${language} runs on our servers, not in the browser`));
  }
  if (language === "python") {
    return runPython(code, testInput, expectedOutput, functionName);
  }
  return engineFor(language as WorkerLanguage).run({
    code,
    testInput,
    expectedOutput,
    functionName: isJsContract(language) ? functionName : "",
    setupCode: language === "sql" ? setupCode : null,
  });
}
